"use client"

import { HomeTile, HomeTileEmpty, type HomeTileLink } from "@/features/dashboard/home-d4/components/home-tile"
import {
  EXPENSE_BAR_CLASS,
  EXPENSE_TEXT_CLASS,
  INCOME_BAR_CLASS,
  INCOME_TEXT_CLASS,
  IncomeExpenseLegend,
} from "@/features/finance/overview/church-f3/components/church-tile-parts"
import { branchBars } from "@/features/finance/overview/church-f3/lib/church-f3-format"
import type { GlobalBranchRow } from "@/features/finance/overview/lib/use-global-branch-rows"
import { useTranslations } from "@/i18n"
import { formatCurrency } from "@/shared/lib/formatting/currency"
import { cn } from "@ibada/ui"
import { FALLBACK_MUTED_CLASS, type TileFallback } from "@/features/finance/overview/church-f3/components/church-tile-parts"


/**
 * Branches (F3): each branch's EARNED income and SPENT expenses this period as two bars on one scale, its net at
 * the right in teal or rose. Rows come already totalled and sorted by the server (best net first).
 */
export function ChurchBranchesTile({
  rows,
  currency,
  link,
  fallback = null,
}: {
  rows: readonly GlobalBranchRow[]
  currency: string
  link: HomeTileLink | null
  /** Set when the tile shows the previous period because this one has no money yet: its label, bars muted. */
  fallback?: TileFallback | null
}) {
  const t = useTranslations("finance.overview")
  const bars = branchBars(rows)

  return (
    <HomeTile
      title={t("widgets.churchBranches.title")}
      subtitle={fallback?.label ?? t("widgets.churchBranches.subtitle")}
      link={link}
      testId='church-tile-branches'
    >
      {bars.length === 0 ? (
        <HomeTileEmpty message={t("church.branches.empty")} />
      ) : (
        <>
          <ul
            data-fallback={fallback ? true : undefined}
            className={cn(
              "-mr-1 flex min-h-0 flex-1 flex-col divide-y divide-border/60 overflow-y-auto pr-1",
              fallback && FALLBACK_MUTED_CLASS,
            )}
          >
            {bars.map((row) => (
              <li
                key={row.id}
                data-testid='church-branch-row'
                className='grid grid-cols-[minmax(0,9.5rem)_minmax(0,1fr)_auto] items-center gap-3 py-[7px]'
                aria-label={t("church.branches.rowLabel", {
                  name: row.name,
                  income: formatCurrency(row.income, currency),
                  expense: formatCurrency(row.expense, currency),
                  net: formatCurrency(row.net, currency),
                })}
              >
                <span className='truncate text-[13px] font-bold text-foreground'>{row.name}</span>
                <span aria-hidden className='flex flex-col gap-[3px]'>
                  <span className={cn("block h-[9px] rounded-[3px]", INCOME_BAR_CLASS)} style={{ width: `${row.incomeRatio * 100}%` }} />
                  <span className={cn("block h-[9px] rounded-[3px]", EXPENSE_BAR_CLASS)} style={{ width: `${row.expenseRatio * 100}%` }} />
                </span>
                <span
                  data-testid='church-branch-net'
                  className={cn(
                    "text-right text-[13px] font-extrabold whitespace-nowrap tabular-nums",
                    row.netIsNegative ? EXPENSE_TEXT_CLASS : INCOME_TEXT_CLASS,
                  )}
                >
                  {row.netIsNegative ? "" : "+"}
                  {formatCurrency(row.net, currency)}
                </span>
              </li>
            ))}
          </ul>
          <IncomeExpenseLegend income={t("church.branches.legendIncome")} expense={t("church.branches.legendExpense")} />
        </>
      )}
    </HomeTile>
  )
}
